import React, { useEffect } from 'react'
import useTreatments from '../../store/treatments/treatmentsContext';
import UploadIcon from '../../components/Icons/outline/UploadIcon';
import AddTreatmentDocumentModal from './AddTreatmentDocumentModal';
import dayjs from 'dayjs';
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import { COLORS, USER_TYPES } from '../../config';
import SecondaryButtonAccent from '../../components/Buttons/SecondaryButtonAccent';
import TrashIcon from '../../components/Icons/solid/TrashIcon';
import TablePaginationBasic from '../ReactTable/TablePaginationBasic';
import SortAscSmall from '../../components/Icons/solid/SortAscSmall';
import SortDescSmall from '../../components/Icons/solid/SortDescSmall';
import DownloadIcon from '../../components/Icons/solid/DownloadIcon';
import useApp from '../../store/app/appContext';

function TreatmentDocuments({
  treatmentId
}) {
  
  //get documents, upload document, delete document
  const {
    getTreatmentDocuments,
    treatmentsState,
    deleteTreatmentDocument,
    downloadTreatmentDocument
  } = useTreatments();

  const {  appState: { user } } = useApp();

  const [showAddTreatmentDocument, setShowAddTreatmentDocument] = React.useState(false);

  // fetch documents
  useEffect(() => {
    getTreatmentDocuments(treatmentId);
  
  }, []);

  //all formatted documents
  const treatmentDocuments = treatmentsState.treatmentDocuments;

  const [minHeight, setMinHeight] = React.useState('auto');

  const data = React.useMemo(
    () => {
      let _data = []
      if (treatmentDocuments.length) {
        treatmentDocuments.map(document => {
          let obj = {
            fileName: document.name,
            lastModified: dayjs(document.last_modified).format('YYYY-MM-DD'),
            size: Math.round(document.size / 1000) + ' KB',
          }
          //if has patientViewable property, add it
          if (document?.hasOwnProperty('patientViewable')) {
            obj["patientViewable"] = document.patientViewable ? "Yes" : "No";
          }
          _data.push(obj)
        })
      };
      _data = _data.sort((a, b) => a.fileName.localeCompare(b.fileName));
      return _data;
    }
  , [treatmentDocuments]);

  useEffect(() => {
    if (data.length) return setMinHeight('502px');
    return setMinHeight('auto');
  }, [data])


  const columns = React.useMemo(
    () => {
      let array = [
      {
        Header: 'File name',
        accessor: 'fileName',
      },
      {
        Header: 'Last modified',
        accessor: 'lastModified',
      },
      {
        Header: 'Size',
        accessor: 'size',
      },
    ]
    if (user.user_type !== USER_TYPES.PATIENT && user.user_type !== USER_TYPES.TEMP) {
      array.push({
        Header: "Patient viewable",
        accessor: 'patientViewable',
      })
    }

    return array
    }, [user]
  );
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    gotoPage,
    prepareRow,
    state : { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 5 }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleDeleteDocument = (row) => {
    const documentName = treatmentDocuments[row].name;
    deleteTreatmentDocument(treatmentId, documentName);
  }

  const handleDownloadDocument = (row) => {
    const documentName = treatmentDocuments[row].name;
    downloadTreatmentDocument(treatmentId, documentName);
  }

  return (
    <div className='bg-white rounded shadow p-10 flex flex-col' style={{ minHeight: minHeight }}>
      <AddTreatmentDocumentModal treatmentId={treatmentId} showAddTreatmentDocument={showAddTreatmentDocument} setShowAddTreatmentDocument={setShowAddTreatmentDocument} user={user}/>
      <div className='flex justify-between'>
        <h1 className="mb-10 text-2xl text-secondary">Documents</h1>
        <SecondaryButtonAccent onClick={() => setShowAddTreatmentDocument(true)} text="Upload file" icon={<UploadIcon fill={COLORS.colorPrimary}/>} />
      </div>
      <div className='w-full mb-auto rounded bg-white'>
        {!!data.length && 
        <div className='overflow-x-scroll'>
          <table {...getTableProps()} className='w-full p-2 xl:p-4 mt-4 border-transparent'>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold'>
                    <span className='flex items-center'>
                    {column.render('Header')}
                    {column.isSorted ? 
                      column.isSortedDesc ? 
                        
                        <SortAscSmall style={'ml-1 h-5 w-5'}/>
                        
                        : <SortDescSmall style={'ml-1 h-5 w-5'} />
                    : ''}
                    </span>
                  </th>
                  ))}
                  <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '></th>
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className=''>
              {page.map(row => {
                prepareRow(row)
                if (page.indexOf(row) === (page.length - 1)) {
                  return (
                    <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      if (cell.column.id === 'fileName') {
                        return (
                          <td {...cell.getCellProps()} className='text-gray-500 font-medium xl:px-2 py-3'>
                          {cell.render('Cell')}
                          </td>
                        )
                      };
                      return (
                        <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                    <td className='text-left xl:px-2'>
                      <div className=' flex flex-row-reverse'>
                        {user.user_type === 'partner' || user.user_type === 'patient' ? '' : <TrashIcon className='ml-2 cursor-pointer' vertical fill={COLORS.colorShade} onClick={() => handleDeleteDocument(row.index)}/>}
                        <DownloadIcon className='ml-2 cursor-pointer' vertical fill={COLORS.colorShade} onClick={() => handleDownloadDocument(row.index)}/>
                      </div>
                    </td>
                    </tr>
                  )
                }
                return (
                  <tr {...row.getRowProps()} className={`border-b-2 border-gray-100`}>
                    {row.cells.map(cell => {
                      if (cell.column.id === 'fileName') {
                        return (
                          <td {...cell.getCellProps()} className='text-gray-500 font-medium xl:px-2 py-3'>
                          {cell.render('Cell')}
                          </td>
                        )
                      };
                      return (
                        <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                    <td className='text-left xl:px-2'>
                      <div className=' flex flex-row-reverse'>
                        {user.user_type === 'partner' || user.user_type === 'patient' ? '' : <TrashIcon className='ml-2 cursor-pointer' vertical fill={COLORS.colorShade} onClick={() => handleDeleteDocument(row.index)}/>}
                        <DownloadIcon className='ml-2 cursor-pointer' vertical fill={COLORS.colorShade} onClick={() => handleDownloadDocument(row.index)}/>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        }
        {!data.length && 
        <span>No documents have been uploaded for this treatment.</span>
        }
      </div>
      {!!data.length && 
      <div className='pt-10 pb-4'>
        <TablePaginationBasic   
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          previousPage={previousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          setPageSize={setPageSize}
          pageSize={pageSize}
          gotoPage={gotoPage}
        />
      </div>
      }
    </div>
  );  
}

export default TreatmentDocuments;
